import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { Search as SearchPatientIcon } from "@mui/icons-material";
import { Box, Grid, Paper, Typography } from '@mui/material';
import Form  from '../../components/general/form';
import SearchFiltersPanel, { SearchFilters } from './SearchFiltersPanel';
import {useHistory, useParams} from 'react-router-dom';
import Loader from '../../components/Loader';
import PatientsTable from '../../components/general/PatientsTable';
import { ButtonBack, ButtonCancel, ButtonContinue, GridContainer } from '../../components/general/mini_components';
import { connect } from 'react-redux';
import { useDispatch } from "react-redux";
import { incrementalSyncPatientsAction, setPatientSearchAction, clearPatientSearchAction } from '../../redux/actions/patientsActions';
import _ from 'lodash';
import Modal from '../../components/general/modal';
import { DepartmentsAccordionRedux } from './departments/DepartmentsAccordion';
import { decryptSinglePatientData, getPatientID, yearsFromDate } from '../../utils/index.jsx';
import ICT from '../../components/general/SmartFields/ICT';
import { searchPatientByDiagnosis } from '../../services';
import { getPatientsWithAppointmentTodayService } from '../../services/agenda';
import PatientAppointmentInfo from './Outpatients/PatientAppointmentInfo';
import { resolveAppointmentPhone } from '../../utils/appointmentPrefill';
import { HOSPITAL_BILLING_PATIENT, HOSPITAL_PATIENT } from '../../routes/urls';
import { TabsSherwood } from '../components/Tabs';
import RequestForm from './Request/RequestForm';
import { ServiceType } from './Request/types';
import { IPatient, PatientStatus, FUNCTIONALITY } from '../../constants/types';
import PatientInfo from '../../components/PatientInfo';
import { fetchPatient } from '../../db';

let personalFieldsForm = {};
const ID_FIELD = {
    name : "id",
    required: false,
    type : "text",
    label : "id",
    order: 0
}

// SHER-353: the filters panel and its SearchFilters shape live in SearchFiltersPanel
// (imported at the top of the file).

function SearchPatients(props: any){
    // Last search persisted in Redux — restored here so results survive the
    // component unmounting when the user drills into a patient and goes back.
    const savedSearch = props.patients.search;
    const [valuesSearch, setValuesSearch] = useState(savedSearch ? savedSearch.valuesSearch : null);
    const [showResults, setShowResults] = useState(savedSearch ? savedSearch.showResults : false);
    const [filteredPatients, setFilteredPatients] = useState<IPatient[]>(savedSearch ? savedSearch.filteredPatients : []);
    const [patientsDiagnoseLoading, setPatientsDiagnoseLoading] = useState(false);
    const [filters, setFilters] = useState<SearchFilters>({ appointmentToday: false });
    // UUID set for the active "appointment today" filter; null = filter off. Held in a ref so
    // it's resolved once per search and read by the worker effect without re-renders.
    const appointmentTodayUuidsRef = useRef<string[] | null>(null);
    const insurances = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.insurances : [];
    const coverages = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.coverages : [];
    const outpatientsInfo = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.outpatientsInfo : {};
    const patients = props.patients.data && props.investigations.currentInvestigation ? props.patients.data[props.investigations.currentInvestigation.uuid] : [];
    const [actionPatient, setActionPatient] = useState<PatientAction | null>(null);
    const [includeDeceased, setIncludeDeceased] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const { action, uuidPatient } = useParams();
    const [selectedPatient, setSelectedPatient] = useState<IPatient | null>(null);
    const [filtering, setFiltering] = useState(false);
    const workerRef = useRef<Worker | null>(null);
    // When a personal-details search was restored from Redux, skip the one
    // mount-time run of the filtering effect below: results are already restored,
    // re-posting to the worker would only flash the loader.
    const skipNextFilter = useRef<boolean>(Boolean(savedSearch && savedSearch.valuesSearch != null));

    // Initialize Web Worker for patient filtering (off main thread)
    useEffect(() => {
        workerRef.current = new Worker(
            new URL('../../workers/patientFilter.worker.ts', import.meta.url),
            { type: 'module' }
        );
        workerRef.current.onmessage = (e) => {
            if (e.data.type === 'results') {
                setFilteredPatients(e.data.patients);
                setShowResults(true);
                setFiltering(false);
            }
        };
        return () => {
            workerRef.current?.terminate();
        };
    }, []);

    // SHER-353: resolve the "appointment today" filter to a uuid set (or null when off).
    // The endpoint returns uuids only (PII stays client-side); the server decides "today" in
    // the agenda timezone. On failure with the filter on, return [] so it matches nothing
    // rather than silently behaving as if it were off.
    async function resolveAppointmentTodayFilter(): Promise<string[] | null> {
        if (!filters.appointmentToday) return null;
        try {
            const response = await getPatientsWithAppointmentTodayService(props.investigations.currentInvestigation.uuid);
            return response.patients.map(p => p.uuid);
        } catch (error) {
            console.error('appointment-today filter failed', error);
            return [];
        }
    }

    async function searchPatientCallBack(values: any) {
        // Resolve active filters BEFORE syncing so the worker effect can intersect on them.
        appointmentTodayUuidsRef.current = await resolveAppointmentTodayFilter();
        setValuesSearch(values);
        // SHER-444: filter local data immediately; sync in the background without blocking UI.
        dispatch(incrementalSyncPatientsAction(props.investigations.currentInvestigation.uuid, true));
    }
    async function diagnoseSelectedCallBack(ict: any) {
        setPatientsDiagnoseLoading(true);
        try {
            const todayUuids = await resolveAppointmentTodayFilter();
            const response = await searchPatientByDiagnosis(props.investigations.currentInvestigation.uuid, ict["ict-code"]);
            let decryptedPatients = response.patients.map((patient: IPatient) => {
                patient.personalData = decryptSinglePatientData(patient.personalData, props.investigations.currentInvestigation);
                return patient;
            });
            if (todayUuids) {
                const todaySet = new Set(todayUuids);
                decryptedPatients = decryptedPatients.filter((p: IPatient) => todaySet.has(p.uuid));
            }
            setFilteredPatients(decryptedPatients);
            setShowResults(true);
        } catch (error) {
            console.error(error);
        } finally {
            setPatientsDiagnoseLoading(false);
        }
    }
    function backToSearchCallBack() {
        setFilteredPatients([]);
        setShowResults(false);
        dispatch(clearPatientSearchAction());
    }
    
    async function patientSelectedCallBack(uuid: string) {
        const nextUrl = HOSPITAL_PATIENT.replace(":uuidPatient", uuid)
        history.push(nextUrl);
    }

    async function billingPatientCallBack(uuid: string) {
        const nextUrl = HOSPITAL_BILLING_PATIENT.replace(":uuidPatient", uuid)
        history.push(nextUrl);
    }

    
    // Dispatch filtering to Web Worker when search criteria or local census changes.
    useEffect(() => {
        if (skipNextFilter.current) {
            // Mount run after restoring a search from Redux — results already restored;
            // re-posting to the worker would only flash the loader.
            skipNextFilter.current = false;
            return;
        }
        if (valuesSearch === null || !workerRef.current) {
            return;
        }
        const hasDeceasedWorkflow = props.investigations.currentInvestigation?.functionalities?.includes("DECEASED_WORKFLOW");
        setFiltering(true);
        workerRef.current.postMessage({
            type: 'filter',
            patients: patients || [],
            searchValues: valuesSearch,
            personalFields: props.investigations.currentInvestigation.personalFields,
            includeDeceased,
            hasDeceasedWorkflow,
            appointmentTodayUuids: appointmentTodayUuidsRef.current,
        });
    }, [valuesSearch, patients, includeDeceased])

    // Persist the search (criteria + results) to Redux whenever results are shown,
    // so navigating back from a patient detail restores them.
    useEffect(() => {
        if (!showResults) {
            return;
        }
        dispatch(setPatientSearchAction({ valuesSearch, showResults, filteredPatients }));
    }, [showResults, filteredPatients])

    // Handle URL parameters
    useEffect(() => {
        if (uuidPatient && patients.length > 0) {
            const patient = patients.find(p => p.uuid === uuidPatient);
            if (patient) {
                setFilteredPatients([patient]);
                setShowResults(true);
                
                if (action) {
                    setActionPatient({action: action, patient: patient});
                }
            }
        }
    }, [uuidPatient, patients, action]);

    if(!props.investigations.data || !props.investigations.currentInvestigation || props.patients.loading || patientsDiagnoseLoading || filtering){
        // The bulk-download progress is rendered by the global blocking overlay in
        // layouts/Dashboard.jsx (driven by patients.downloading). This guard keeps
        // the page from rendering with partial data while patients.loading is set
        // by other flows (saves, incremental syncs) and covers page-local states
        // (filtering, diagnoses).
        return <Loader />;
    }
    return(
        <React.Fragment>
            <SearchPatientsComponent showResults={showResults}
                patients={filteredPatients} investigation={props.investigations.currentInvestigation}
                personalFields={props.investigations.currentInvestigation.personalFields}
                permissions={props.investigations.currentInvestigation.permissions}
                functionalities={props.investigations.currentInvestigation.functionalities}
                insurances={insurances} coverages={coverages} outpatientsInfo={outpatientsInfo}
                initActionPatient={actionPatient}
                includeDeceased={includeDeceased}
                setIncludeDeceased={setIncludeDeceased}
                searchPatientCallBack={searchPatientCallBack}
                filters={filters}
                setFilters={setFilters}
                backToSearchCallBack={backToSearchCallBack}
                patientSelectedCallBack={patientSelectedCallBack}
                billingPatientCallBack={billingPatientCallBack}
                diagnoseSelectedCallBack={diagnoseSelectedCallBack}
                    />
        </React.Fragment>                          
    )
}

const mapStateToProps = (state) =>{
    return {
        patients : state.patients,
        investigations:state.investigations
    }
}

SearchPatients.propTypes = {
    personalFields:PropTypes.array,
}

export default connect(mapStateToProps, null)(SearchPatients)

type PatientAction = {
    action: 'appointment' | 'pre-hospitalize' | 'hospitalize' | 'surgery' | string; // string for future actions
    patient: IPatient;
  }

interface SearchPatientsProps extends LocalizeContextProps {
    showResults: boolean;
    patients: IPatient[];
    investigation: any;
    personalFields: any[];
    permissions: any[];
    functionalities: any[];
    insurances: any[];
    coverages: any[];
    outpatientsInfo: any;
    initActionPatient: PatientAction | null;
    includeDeceased: boolean;
    setIncludeDeceased: (value: boolean) => void;
    searchPatientCallBack: (values: any) => void;
    filters: SearchFilters;
    setFilters: (filters: SearchFilters) => void;
    backToSearchCallBack: () => void;
    patientSelectedCallBack: (uuid: string) => void;  
    diagnoseSelectedCallBack: (ict: any) => void;
    billingPatientCallBack: (uuid: string) => void;
}


export const SearchPatientsComponent = withLocalize((props: SearchPatientsProps) => {
    const [actionPatient, setActionPatient] = useState<PatientAction | null>(props.initActionPatient);


    useEffect(() => {
        setActionPatient(props.initActionPatient);
    }, [props.initActionPatient]);

    function backToSearch(){
        props.backToSearchCallBack()
    }

    async function hospitalizePatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({patient: fetchedPatient, action : "pre-hospitalize"});
        }
    }
    async function billingPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            props.billingPatientCallBack(fetchedPatient.uuid);
        }
    }

    async function selectAppointmentPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({action: "appointment", patient: fetchedPatient});
        }
    }

    async function surgeryPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({action: "surgery", patient: fetchedPatient});
        }
    }
    
    function confirm(){
        setActionPatient({...actionPatient, action: "hospitalize"});
    }
    function resetModal(){
        setActionPatient(null);
    }

    function renderModal(){
        if(actionPatient?.patient){
            return (
                <Modal key="modal" open={Boolean(actionPatient.patient)} 
                    title={<Translate id={`hospital.search_patient.modal.${actionPatient.action}.title`}/>} 
                    closeModal={resetModal}
                >
                            <>
                            {
                            (actionPatient && actionPatient.action === 'pre-hospitalize')&&
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography variant="body1">
                                        <Translate id="hospital.search_patient.modal.pre-hospitalize.body" />

                                    </Typography>
                                    <PatientInfo uuidPatient={actionPatient.patient.uuid} />
                                </Grid>
                                <Grid item xs={12} style={{paddingTop:'1rem'}}>
                                    <ButtonCancel onClick={resetModal} data-testid="cancel-modal" color="primary" spaceright={1}>
                                        <Translate id="general.cancel" />
                                    </ButtonCancel>
                                    &nbsp;
                                    <ButtonContinue onClick={confirm} data-testid="continue-modal" color="green">
                                        <Translate id="general.continue" />
                                    </ButtonContinue>
                                </Grid>
                                
                            </Grid>
                        }
                        {
                            actionPatient && actionPatient.action === "hospitalize" &&
                            <DepartmentsAccordionRedux currentInvestigation={props.investigation} 
                                
                                uuidPatient={actionPatient.patient.uuid} />
                        }
                        {
                            actionPatient && actionPatient.action === "appointment" &&
                            <PatientAppointmentInfo uuidInvestigation={props.investigation.uuid}  uuidPatient={actionPatient.patient.uuid} 
                                outpatientsInfo={props.outpatientsInfo} phoneNumber={resolveAppointmentPhone(props.outpatientsInfo, actionPatient.patient.personalData.phone)}
                                resetModal={resetModal}
                                appointmentMadeCallback={resetModal} />
                        }
                        {
                            actionPatient && actionPatient.action === "surgery" &&
                            <RequestForm uuidInvestigation={props.investigation.uuid}  uuidPatient={actionPatient.patient.uuid} 
                                serviceType={ServiceType.SURGERY} cancel={resetModal}
                                units={[]}
                                callBackRequestFinished={resetModal}/> 
                        }
                        </>
                </Modal>
            )   
        }
        else{
            return null;
        }
        
    }

    function renderCore(){
        if(!props.showResults){
            let personalFieldsForm = {}
            props.personalFields
                .filter((p: any) => p.type !== 'hidden')
                .sort((a,b) => a.order - b.order).forEach(personalField => {
                            const copyField = {...personalField};
                            copyField.required = false;
                            // Lay the search fields out in two columns from the lg breakpoint up so the
                            // form fills the available width instead of running as a single narrow strip
                            // on wide desktop screens. Stays one column on xs/sm/md (Form defaults xs=12).
                            // Respect any per-field override the field config already carries.
                            if(copyField.numberColumnsLg === undefined){
                                copyField.numberColumnsLg = 6;
                            }
                            personalFieldsForm[personalField.name] = copyField
                        });
            if(Object.keys(personalFieldsForm).length === 0){
                personalFieldsForm["id"] = ID_FIELD;
            }
            
            const hasDeceasedWorkflow = props.functionalities?.includes("DECEASED_WORKFLOW");
            const hasOutpatients = props.functionalities?.includes(FUNCTIONALITY.OUTPATIENTS);
            // The filters panel renders if ANY filter applies (see SearchFiltersPanel's gate). The
            // search column must narrow to make room whenever the panel shows — keying this off
            // hasOutpatients alone left the form at full width while a deceased-only panel still
            // claimed its 4 columns, overflowing the 12-col grid and wrapping the panel below.
            const hasFilters = hasOutpatients || hasDeceasedWorkflow;
            const tabLabels: any[] = [
                <Translate id="pages.hospital.search-patient.by-personal-details" />,
                <Translate id="pages.hospital.search-patient.by-diagnose" />,
            ];
            const tabPanels: any[] = [
                <Box label={<Translate id="pages.hospital.search-patient.by-personal-details" />}>
                    <Paper style={{padding:'1rem', margin:'1rem'}}>
                        <Translate id="pages.hospital.search-patient.note" />
                        <Form fields={personalFieldsForm} fullWidth
                            submitText="investigation.search-patients.search" callBackForm={props.searchPatientCallBack} />
                    </Paper>
                </Box>,
                <Box label={<Translate id="pages.hospital.search-patient.by-diagnose" />}>
                    <Paper style={{padding:'1rem', margin:'1rem'}}>
                        <ICT language={props.activeLanguage.code} elementSelected={props.diagnoseSelectedCallBack}/>
                    </Paper>
                </Box>,
            ];
            // Search form is the hero (wide); the extensible filters live in a narrower right-hand
            // sidebar so the panel reads as a deliberate aside rather than a tiny box floating in
            // empty space. Filters intersect with whatever the active search returns (name → worker,
            // diagnosis → server). When the tenant has no filters the panel renders null, so the
            // search column claims the full width on its own.
            return (
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={hasFilters ? 8 : 12} lg={hasFilters ? 9 : 12}>
                        <TabsSherwood initTab={0} name="Search Patients" labels={tabLabels}>
                            {tabPanels}
                        </TabsSherwood>
                    </Grid>
                    {/* Drop the filters column down by the tab bar (~48px) + TabPanel padding
                        (theme.spacing(3) = 24px) + the search card's own padding (1rem) so the
                        "Filters" title lines up with the "Hit search…" note text, not just the card
                        border. Only from md up, where the columns are side by side; on xs/sm the panel
                        stacks under the form and needs no offset. */}
                    <Grid item xs={12} md={4} lg={3} sx={{ mt: { md: 11 } }}>
                        <SearchFiltersPanel filters={props.filters} setFilters={props.setFilters} hasOutpatients={hasOutpatients}
                            hasDeceasedWorkflow={hasDeceasedWorkflow} includeDeceased={props.includeDeceased} setIncludeDeceased={props.setIncludeDeceased} />
                    </Grid>
                </Grid>
            );
        }
        else{
            if(props.patients.length === 0){
                return ([
                    <Box mb={2}>
                        <Paper style={{padding:'1rem'}}>
                            No patients meet the criteria
                        </Paper>
                    </Box>,    
                    <ButtonBack mt={2} onClick={backToSearch}><Translate id="pages.hospital.search-patient.back-button" /></ButtonBack>,
                ])
            }
            else{
                let formSearch = [...props.personalFields].filter((p: any) => p.type !== 'hidden')
                if(formSearch.length === 0){
                    formSearch.push(ID_FIELD);
                } 
                console.log("Patients Results", props.patients);
                return(
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <PatientsTable patients={props.patients} 
                                personalFields={formSearch} permissions={props.permissions}
                                functionalities={props.functionalities}
                                insurances={props.insurances ? props.insurances : []}
                                coverages={props.coverages ? props.coverages : []}
                                showPatientCallBack={id => props.patientSelectedCallBack(id)} 
                                selectAppointmentPatientCallBack={(index) => selectAppointmentPatient(index)}
                                billingPatientCallBack={(index) => billingPatient(index)}
                                hospitalizePatientCallBack={(index) => hospitalizePatient(index)} 
                                surgeryPatientCallBack={(index) => surgeryPatient(index)}
                                />
                        </Grid>
                        <Grid item xs={12}>
                            <ButtonBack onClick={backToSearch}><Translate id="pages.hospital.search-patient.back-button" /></ButtonBack>
                        </Grid>
                    </Grid>)
            }
        }
    }
   
    
    return (
        <React.Fragment>
            {
                renderModal()
            }
            <Grid container spacing={2} >
                <GridContainer item xs={12}>
                    <SearchPatientIcon style={{fontSize:"2.5rem"}} />
                    <Typography variant="h1" gutterBottom display="inline" style={{marginBottom:"0px"}}>
                        <Translate id="pages.hospital.search-patient.title" />
                    </Typography>
                </GridContainer>
                
                <Grid item xs={12}>
                    {
                        renderCore()
                    }     
                </Grid>         
            </Grid>
        </React.Fragment>
    )
})